import { NextPage } from 'next'
import { useTranslation } from '../../utils/convergence'
import React, { ReactElement, useEffect, useState } from 'react'
import { Head, Verify } from '../../../src/component'
import { Route, useRoutePageName } from '../../../src/config/router'
import { TrackPageViewed } from '../../../src/integrations/monitoring'
import { SupportedLocale, VerificationErrorCode } from '../../../src/model'
import {
  captureCashConsent,
  setCheckoutError,
  setDisplayProcessingModal,
  setIsMobileLogin,
  useAppDispatch,
  useAppRouter,
  useVerificationFlow,
} from '../../../src/state'
import { CashSignInTerms } from '../../../src/component/CashSignInTerms'
import { getBackRoute, getCheckoutError } from './helper'

/**
 * Verify page is used to send & validate codes via email or sms
 */
export const VerifyPage: NextPage = (): ReactElement => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const router = useAppRouter()
  const [disabled, setDisabled] = useState(false)
  const { flow, showCashTerms } = useVerificationFlow()
  const pageName = useRoutePageName()

  const onSuccess = (route?: Route, locale?: SupportedLocale, triggerProcessingModal: boolean = false): void => {
    if (showCashTerms) {
      dispatch(captureCashConsent())
    }
    if (triggerProcessingModal) {
      dispatch(setDisplayProcessingModal(true))
      return
    }

    if (route !== undefined) {
      router.push(route, locale)
      return
    }

    router.push(Route.TERMINAL_ERROR)
  }

  const resetDisabledState = (): void => {
    setDisabled(false)
  }

  const onTerminalError = (e?: VerificationErrorCode): void => {
    if (e === undefined) {
      return router.push(Route.TERMINAL_ERROR)
    }

    dispatch(setCheckoutError(getCheckoutError(e)))
    router.push(Route.TERMINAL_ERROR)
  }

  const onComplete = (): void => {
    setDisabled(true)
  }

  const onError = (): void => {
    dispatch(setIsMobileLogin(false))
    const route = getBackRoute(flow.context)
    router.push(route)
  }

  const pageTitle = `${pageName} | ${t('common:brandName')}`

  useEffect(() => {
    router.prefetch(Route.TERMINAL_ERROR)

    flow.prefetchRoutes.forEach((route): void => {
      router.prefetch(route)
    })
  }, [])

  return (
    <>
      <Head title={pageTitle} />
      <Verify
        onSuccess={onSuccess}
        resetDisabledState={resetDisabledState}
        onTerminalError={onTerminalError}
        onComplete={onComplete}
        disabled={disabled}
        flow={flow}
        onError={onError}
      />
      {showCashTerms && <CashSignInTerms />}
      <TrackPageViewed eventProperties={flow.enabled} />
    </>
  )
}
